import React, { useState } from "react";
import {
  Box, Paper, TextField, Button, Typography, Alert, CircularProgress,
  Link, Chip, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "../components/Footer";
import SupportFab from "../components/SupportFab";

function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cnpjInput, setCnpjInput] = useState("");
  const [cnpjsValidados, setCnpjsValidados] = useState([]);
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const formatCnpj = (value) => {
    const nums = value.replace(/\D/g, "");
    return nums.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  };

  const handleValidateCnpj = async () => {
    const cnpj = cnpjInput.replace(/\D/g, "");
    if (cnpj.length !== 14) { setError("CNPJ deve ter 14 digitos"); return; }
    if (cnpjsValidados.find(c => c.cnpj === cnpj)) { setError("CNPJ ja adicionado"); return; }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post("/api/customer-portal/validate-cnpj", { cnpj });
      if (response.data.valid) {
        setCnpjsValidados([...cnpjsValidados, { cnpj, razaoSocial: response.data.razaoSocial, fonte: response.data.fonte }]);
        setCnpjInput("");
        setSuccess("CNPJ validado!");
        setTimeout(() => setSuccess(""), 3000);
      } else { setError("CNPJ nao encontrado no sistema"); }
    } catch (err) { setError(err.response?.data?.message || "Erro ao validar CNPJ"); }
    finally { setLoading(false); }
  };

  const handleRemoveCnpj = (cnpj) => setCnpjsValidados(cnpjsValidados.filter(c => c.cnpj !== cnpj));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError("As senhas nao conferem"); return; }
    if (password.length < 6) { setError("Senha deve ter pelo menos 6 caracteres"); return; }
    if (cnpjsValidados.length === 0) { setError("Adicione pelo menos um CNPJ"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post("/api/customer-portal/register", { email, nome, password, cnpjs: cnpjsValidados.map(c => c.cnpj) });
      setStep(3);
    } catch (err) { setError(err.response?.data?.message || "Erro ao cadastrar"); }
    finally { setLoading(false); }
  };

  if (step === 3) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          <Paper sx={{ p: 4, maxWidth: 500, width: "100%", textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: "secondary.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>Cadastro realizado!</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Seu cadastro foi enviado para aprovação. Você receberá uma notificação assim que for liberado.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate("/")}>Voltar para Login</Button>
          </Paper>
        </Box>
        <SupportFab />
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2, py: 4 }}>
      <Paper sx={{ p: 4, maxWidth: 500, width: "100%" }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: "12px", mx: "auto", mb: 1.5,
            bgcolor: "primary.main", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 28,
            boxShadow: "0 4px 10px rgba(21,101,192,0.25)"
          }}>O</Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.dark" }}>Criar conta</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {step === 1 ? "Adicione os CNPJs que deseja acessar" : "Complete seus dados"}
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {step === 1 && (
          <>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField fullWidth label="CNPJ" value={cnpjInput} onChange={(e) => setCnpjInput(e.target.value)} placeholder="00.000.000/0000-00" disabled={loading} />
              <Button variant="contained" onClick={handleValidateCnpj} disabled={loading || cnpjInput.replace(/\D/g, "").length !== 14}>
                {loading ? <CircularProgress size={24} /> : "Validar"}
              </Button>
            </Box>
            {cnpjsValidados.length > 0 && (
              <List sx={{ mb: 2 }}>
                {cnpjsValidados.map((item) => (
                  <ListItem key={item.cnpj} sx={{ bgcolor: "#e8f5e9", mb: 1, borderRadius: 1 }}>
                    <ListItemText primary={item.razaoSocial} secondary={formatCnpj(item.cnpj)} />
                    <ListItemSecondaryAction>
                      <Chip label={item.fonte} size="small" sx={{ mr: 1 }} />
                      <IconButton edge="end" onClick={() => handleRemoveCnpj(item.cnpj)}><DeleteIcon /></IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
            <Button fullWidth variant="contained" size="large" disabled={cnpjsValidados.length === 0} onClick={() => setStep(2)} sx={{ mt: 2 }}>Continuar</Button>
          </>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <Alert severity="info" sx={{ mb: 2 }}>CNPJs selecionados: {cnpjsValidados.length}</Alert>
            <TextField fullWidth label="Nome Completo" value={nome} onChange={(e) => setNome(e.target.value)} margin="normal" required />
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" required />
            <TextField fullWidth label="Senha" type="password" value={password} onChange={(e) => setPassword(e.target.value)} margin="normal" required helperText="Minimo 6 caracteres" />
            <TextField fullWidth label="Confirmar Senha" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} margin="normal" required />
            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={() => setStep(1)} sx={{ flex: 1 }}>Voltar</Button>
              <Button type="submit" variant="contained" disabled={loading} sx={{ flex: 1 }}>{loading ? <CircularProgress size={24} /> : "Cadastrar"}</Button>
            </Box>
          </form>
        )}
        <Typography variant="body2" align="center" sx={{ mt: 3 }}>Já tem cadastro? <Link href="/" underline="hover">Faça login</Link></Typography>
      </Paper>
      </Box>
      <SupportFab />
      <Footer />
    </Box>
  );
}

export default Register;
